import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuEye, LuEyeOff, LuLoaderCircle, LuLock, LuLogIn, LuMail, LuScanFace, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import { GLASS_CHIP, GLASS_PRESSABLE } from '../../components/ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';

// 16px text on phones (iOS zooms into smaller fields); autofill keeps the card's colours
const FIELD = [
  'tw-h-12 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/80 tw-pl-11 tw-text-base tw-text-slate-900 tw-shadow-none tw-outline-none tw-transition',
  'placeholder:tw-text-slate-400 focus:tw-border-teal-500/60 focus:tw-ring-4 focus:tw-ring-teal-500/15 disabled:tw-opacity-60 sm:tw-text-[15px]',
  '[&:-webkit-autofill]:tw-shadow-[inset_0_0_0_1000px_#ffffff] [&:-webkit-autofill]:[-webkit-text-fill-color:#0f172a]',
  'dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-text-white dark:placeholder:tw-text-slate-500 dark:focus:tw-border-teal-400/50 dark:focus:tw-ring-teal-400/15',
  'dark:[&:-webkit-autofill]:tw-shadow-[inset_0_0_0_1000px_#111827] dark:[&:-webkit-autofill]:[-webkit-text-fill-color:#ffffff]',
].join(' ');

const FieldIcon = ({ icon: Icon }) => (
  <Icon aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-4 tw-top-1/2 tw-h-[18px] tw-w-[18px] -tw-translate-y-1/2 tw-text-slate-400 dark:tw-text-slate-500" />
);

FieldIcon.propTypes = { icon: PropTypes.elementType.isRequired };

/** First-time face login: sign in with the password once, then register the face. */
const SetupBanner = ({ onCancel, onTryAnyway }) => {
  const { t } = useTranslation();
  return (
    <div role="status" className="tw-rounded-2xl tw-border tw-border-teal-500/25 tw-bg-teal-500/10 tw-p-4 dark:tw-border-teal-400/20 dark:tw-bg-teal-400/10">
      <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200">
        <LuScanFace aria-hidden="true" className="tw-h-[18px] tw-w-[18px] tw-shrink-0" />
        {t('login.setup.title')}
      </p>
      <p className="tw-mt-1.5 tw-text-[13px] tw-leading-relaxed tw-text-teal-900/80 dark:tw-text-teal-100/80">{t('login.setup.body')}</p>
      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1.5 tw-text-xs tw-font-semibold">
        <button type="button" onClick={onCancel} className="tw-rounded tw-bg-transparent tw-text-teal-800 hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-200">
          {t('login.setup.cancel')}
        </button>
        <button type="button" onClick={onTryAnyway} className="tw-rounded tw-bg-transparent tw-text-teal-700/80 hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300/80">
          {t('login.setup.tryAnyway')}
        </button>
      </div>
    </div>
  );
};

SetupBanner.propTypes = { onCancel: PropTypes.func.isRequired, onTryAnyway: PropTypes.func.isRequired };

/** E-mail + password, face login and "Şifremi Unuttum". */
const LoginForm = ({ flow, passkeyAvailable, onForgot }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const busy = flow.isLoading || flow.passkeyBusy;

  const trackCapsLock = (event) => {
    if (typeof event.getModifierState === 'function') setCapsLock(event.getModifierState('CapsLock'));
  };

  return (
    <form onSubmit={flow.submitPassword} className="tw-space-y-4">
      <div className="tw-text-center">
        <h1 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{t('login.title')}</h1>
        <p className="tw-mt-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t('login.subtitle')}</p>
      </div>

      {flow.setupIntent && <SetupBanner onCancel={flow.cancelSetup} onTryAnyway={() => flow.startFaceLogin(true)} />}

      <div className="tw-space-y-1.5">
        <label htmlFor="login-email" className="tw-block tw-px-1 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{t('login.email')}</label>
        <div className="tw-relative">
          <FieldIcon icon={LuMail} />
          <input
            id="login-email"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={flow.email}
            onChange={(event) => flow.setEmail(event.target.value)}
            disabled={busy}
            placeholder={t('login.emailPlaceholder')}
            className={`${FIELD} tw-pr-4`}
          />
        </div>
      </div>

      <div className="tw-space-y-1.5">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-1">
          <label htmlFor="login-password" className="tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{t('login.password')}</label>
          {capsLock && <span className="tw-text-[11px] tw-font-semibold tw-text-amber-600 dark:tw-text-amber-300">{t('login.capsLock')}</span>}
        </div>
        <div className="tw-relative">
          <FieldIcon icon={LuLock} />
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            autoFocus={Boolean(flow.email)}
            value={flow.password}
            onChange={(event) => flow.setPassword(event.target.value)}
            onKeyDown={trackCapsLock}
            onKeyUp={trackCapsLock}
            onBlur={() => setCapsLock(false)}
            disabled={busy}
            placeholder={t('login.passwordPlaceholder')}
            className={`${FIELD} tw-pr-12`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((value) => !value)}
            aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
            title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
            className="tw-absolute tw-right-1.5 tw-top-1/2 tw-grid tw-h-9 tw-w-9 -tw-translate-y-1/2 tw-place-items-center tw-rounded-xl tw-bg-transparent tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white"
          >
            {showPassword ? <LuEyeOff aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuEye aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
          </button>
        </div>
      </div>

      {flow.error && (
        <p role="alert" className="tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-bg-rose-500/10 tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-rose-700 dark:tw-text-rose-300">
          <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
          {flow.error}
        </p>
      )}

      <Button variant="primary" size="lg" type="submit" disabled={busy} className="tw-w-full tw-text-[15px]">
        {flow.isLoading
          ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          : <LuLogIn aria-hidden="true" className="tw-h-4 tw-w-4" />}
        {flow.isLoading ? t('login.submitting') : t('login.submit')}
      </Button>

      {passkeyAvailable && !flow.setupIntent && (
        <>
          <div className="tw-flex tw-items-center tw-gap-3" aria-hidden="true">
            <span className="tw-h-px tw-flex-1 tw-bg-slate-200 dark:tw-bg-white/10" />
            <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.16em] tw-text-slate-400 dark:tw-text-slate-500">{t('login.or')}</span>
            <span className="tw-h-px tw-flex-1 tw-bg-slate-200 dark:tw-bg-white/10" />
          </div>
          <div className="tw-space-y-1.5">
            <button
              type="button"
              onClick={() => flow.startFaceLogin()}
              disabled={busy}
              className={`tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-gap-2.5 tw-rounded-full tw-text-[15px] tw-font-semibold tw-text-slate-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 dark:tw-text-slate-100 ${GLASS_CHIP} ${GLASS_PRESSABLE}`}
            >
              {flow.passkeyBusy
                ? <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-600 dark:tw-text-teal-300" />
                : <LuScanFace aria-hidden="true" className="tw-h-5 tw-w-5 tw-text-teal-600 dark:tw-text-teal-300" />}
              {flow.passkeyBusy ? t('login.faceBusy') : t('login.face')}
            </button>
            <p className="tw-text-center tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{t('login.faceHint')}</p>
          </div>
        </>
      )}

      <div className="tw-text-center">
        <button
          type="button"
          onClick={onForgot}
          disabled={busy}
          className="tw-rounded tw-bg-transparent tw-text-sm tw-font-semibold tw-text-teal-700 tw-underline-offset-4 hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-opacity-50 dark:tw-text-teal-300"
        >
          {t('login.forgot')}
        </button>
      </div>
    </form>
  );
};

LoginForm.propTypes = {
  flow: PropTypes.object.isRequired,
  passkeyAvailable: PropTypes.bool,
  onForgot: PropTypes.func.isRequired,
};

export default LoginForm;
